import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step1", "step2", "step3",
    "step1Indicator", "step2Indicator", "step3Indicator",
    "step1Next", "customerSelect", "orderItems",
    "reviewCustomer", "reviewProducts", "reviewTotalItems", "reviewTotal"
  ]

  connect() {
    this.currentStep = 1
    this.updateStepIndicators()
  }

  nextStep(event) {
    event.preventDefault()

    // Validate current step
    if (!this.validateCurrentStep()) {
      return
    }

    if (this.currentStep < 3) {
      this.currentStep++
      this.showStep(this.currentStep)

      // Update review on step 3
      if (this.currentStep === 3) {
        this.updateReview()
      }
    }
  }

  prevStep(event) {
    event.preventDefault()

    if (this.currentStep > 1) {
      this.currentStep--
      this.showStep(this.currentStep)
    }
  }

  goToStep(event) {
    event.preventDefault()
    const targetStep = parseInt(event.currentTarget.dataset.step)

    if (targetStep === this.currentStep) return

    // Allow going back to any previous step
    if (targetStep < this.currentStep) {
      this.currentStep = targetStep
      this.showStep(this.currentStep)
    }
    // Allow going forward one step if valid (same as clicking Next)
    else if (targetStep === this.currentStep + 1) {
      if (this.validateCurrentStep()) {
        this.currentStep++
        this.showStep(this.currentStep)
        if (this.currentStep === 3) {
          this.updateReview()
        }
      }
    }
  }

  showStep(step) {
    // Hide all steps
    this.step1Target.style.display = 'none'
    this.step2Target.style.display = 'none'
    this.step3Target.style.display = 'none'

    // Show current step
    if (step === 1) this.step1Target.style.display = 'block'
    if (step === 2) this.step2Target.style.display = 'block'
    if (step === 3) this.step3Target.style.display = 'block'

    this.updateStepIndicators()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  updateStepIndicators() {
    const indicators = [this.step1IndicatorTarget, this.step2IndicatorTarget, this.step3IndicatorTarget]

    indicators.forEach((el, index) => {
      const stepNum = index + 1
      // Find sibling label within the .wizard-step container
      const container = el.closest('.wizard-step')
      const label = container ? container.querySelector('.wizard-label') : null

      // Reset
      el.classList.remove('active', 'completed')
      el.innerHTML = stepNum // Reset content (in case checkmark was there)
      if (label) label.classList.remove('active')

      if (this.currentStep > stepNum) {
        el.classList.add('completed')
        el.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>'
      }

      if (this.currentStep === stepNum) {
        el.classList.add('active')
        if (label) label.classList.add('active')
      }
    })
  }

  validateCurrentStep() {
    if (this.currentStep === 1) {
      // Validate customer selection
      const customerSelect = this.customerSelectTarget

      // Check if SearchableSelect is active (hidden select)
      const isSearchable = customerSelect.style.display === 'none'

      if (!customerSelect.value || customerSelect.value === '') {
        if (isSearchable) {
          // Check if user typed something but didn't select
          const wrapper = customerSelect.nextElementSibling
          const input = wrapper ? wrapper.querySelector('input') : null

          if (input && input.value.trim() !== '') {
            alert('Please select a customer from the dropdown list. You must click an option.')
          } else {
            alert('Please select a customer before continuing')
          }

          if (input) input.focus()
        } else {
          alert('Please select a customer before continuing')
          customerSelect.focus()
        }
        return false
      }
    }

    if (this.currentStep === 2) {
      // Validate at least one product
      const productSelects = this.orderItemsTarget.querySelectorAll('select[name*="[product_id]"]')
      let hasValidProduct = false

      productSelects.forEach(select => {
        if (select.value && select.value !== '') {
          hasValidProduct = true
        }
      })

      if (!hasValidProduct) {
        alert('Please add at least one product to the order')
        return false
      }
    }

    return true
  }

  addProduct(event) {
    event.preventDefault()

    // Clone the first order item row
    const firstRow = this.orderItemsTarget.querySelector('.order-item-row')
    if (!firstRow) return

    const newRow = firstRow.cloneNode(true)

    // Remove any existing searchable select wrappers from the clone
    newRow.querySelectorAll('.searchable-select-wrapper').forEach(wrapper => wrapper.remove())

    // Reset select visibility (it will be hidden again by the controller)
    newRow.querySelectorAll('select').forEach(select => {
      select.style.display = ''
    })

    // Clear values
    newRow.querySelectorAll('input, select').forEach(input => {
      input.value = ''
    })

    // Update field names to have unique index
    const timestamp = new Date().getTime()
    newRow.querySelectorAll('[name]').forEach(field => {
      const name = field.getAttribute('name')
      field.setAttribute('name', name.replace(/\[\d+\]/, `[${timestamp}]`))
    })

    this.orderItemsTarget.appendChild(newRow)
  }

  updateReview() {
    // Update customer name
    const customerSelect = this.customerSelectTarget
    const customerName = customerSelect.options[customerSelect.selectedIndex]?.text || '—'
    this.reviewCustomerTarget.textContent = customerName

    // Calculate products and totals
    const productRows = this.orderItemsTarget.querySelectorAll('.order-item-row')
    let productsList = []
    let totalItems = 0
    let totalPrice = 0

    productRows.forEach(row => {
      const productSelect = row.querySelector('select[name*="[product_id]"]')
      const quantityInput = row.querySelector('input[name*="[quantity]"]')
      const priceInput = row.querySelector('input[name*="[unit_price]"]')

      if (productSelect && productSelect.value) {
        const productName = productSelect.options[productSelect.selectedIndex]?.text
        const quantity = parseInt(quantityInput?.value) || 0
        const price = parseFloat(priceInput?.value) || 0

        if (productName && quantity > 0) {
          productsList.push(`${productName} (×${quantity})`)
          totalItems += quantity
          totalPrice += (quantity * price)
        }
      }
    })

    this.reviewProductsTarget.innerHTML = productsList.join('<br>') || '—'
    this.reviewTotalItemsTarget.textContent = totalItems
    this.reviewTotalTarget.textContent = `$${totalPrice.toFixed(2)}`
  }
}
